/*=============================================== PaginatorPage ===============================================*/

import { Link } from "react-router-dom"
import { Text } from "tsx-library-julseb"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import { commonProps, iconBoxicon } from "data"

const PaginatorPage = () => {
    const items: ItemType[] = [
        {
            title: "Paginator",
            intro: commonProps.routerOnly,
            content: (
                <Text>
                    <Link
                        to="/components/paginator/demo"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() && (\n    <Paginator\n        totalPages={getNumberOfPages()}\n        icons={{ prev: "arrow-left", next: "arrow-right" }}\n    />\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() && (\n    <Paginator\n        totalPages={getNumberOfPages()}\n        icons={{ prev: "arrow-left", next: "arrow-right" }}\n    />\n)}',
        },
        {
            title: "Paginator with query",
            intro: commonProps.routerOnly,
            content: (
                <Text>
                    <Link
                        to="/components/paginator/demo-query"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\nconst searchParams = [\n    ["search", "hello"],\n    ["order", "asc"],\n]\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() && (\n    <Paginator\n        totalPages={getNumberOfPages()}\n        queries={searchParams}\n        isInputEditable={false}\n    />\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst { getNumberOfPages, getPaginatedData } = usePaginatedData(arr)\n\nconst searchParams: string[][] = [\n    ["search", "hello"],\n    ["order", "asc"],\n]\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{getNumberOfPages() && (\n    <Paginator\n        totalPages={getNumberOfPages()}\n        queries={searchParams}\n        isInputEditable={false}\n    />\n)}',
        },
        {
            title: "Custom navigation",
            content: (
                <Text>
                    <Link
                        to="/components/paginator/demo-custom"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Demo here.
                    </Link>
                </Text>
            ),
            // eslint-disable-next-line
            code: 'const arr = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst dataLimit = 20\n\nconst totalPages = Math.ceil((arr?.length || 0) / dataLimit)\n\nconst [searchParams, setSearchParams] = useSearchParams()\nconst page = searchParams.get("page") || "1"\n\nconst [currentPage, setCurrentPage] = useState(parseInt(page))\n\nconst handlePrev = () => {\n    setSearchParams({ page: (currentPage - 1).toString() })\n    setCurrentPage(currentPage - 1)\n    scrollToTop()\n}\n\nconst handleNext = () => {\n    setSearchParams({ page: (currentPage + 1).toString() })\n    setCurrentPage(currentPage + 1)\n    scrollToTop()\n}\n\nconst handlePage = e => {\n    const value = parseInt(e.target.value)\n\n    const selectedPage =\n        value > totalPages\n            ? totalPages\n            : value <= 1 || !e.target.value\n            ? 1\n            : value\n\n    setSearchParams({ page: selectedPage.toString() })\n    setCurrentPage(selectedPage)\n    scrollToTop()\n}\n\nconst getPaginatedData = () => {\n    const startIndex = currentPage * dataLimit - dataLimit\n    const endIndex = startIndex + dataLimit\n    return arr?.slice(startIndex, endIndex)\n}\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{totalPages > 1 && (\n    <Paginator\n        totalPages={totalPages}\n        page={currentPage}\n        handlePrevPage={handlePrev}\n        handleNextPage={handleNext}\n        handleInput={handlePage}\n    />\n)}',
            // eslint-disable-next-line
            codeTs: 'const arr: string[] = generateNumbers(1, 201).map((n) => `Hello ${n}`)\nconst dataLimit: number = 20\n\nconst totalPages: number = Math.ceil((arr?.length || 0) / dataLimit)\n\nconst [searchParams, setSearchParams] = useSearchParams()\nconst page = searchParams.get("page") || "1"\n\nconst [currentPage, setCurrentPage] = useState<number>(parseInt(page))\n\nconst handlePrev = () => {\n    setSearchParams({ page: (currentPage - 1).toString() })\n    setCurrentPage(currentPage - 1)\n    scrollToTop()\n}\n\nconst handleNext = () => {\n    setSearchParams({ page: (currentPage + 1).toString() })\n    setCurrentPage(currentPage + 1)\n    scrollToTop()\n}\n\nconst handlePage = (e: ChangeEvent<HTMLInputElement>) => {\n    const value = parseInt(e.target.value)\n\n    const selectedPage =\n        value > totalPages\n            ? totalPages\n            : value <= 1 || !e.target.value\n            ? 1\n            : value\n\n    setSearchParams({ page: selectedPage.toString() })\n    setCurrentPage(selectedPage)\n    scrollToTop()\n}\n\nconst getPaginatedData = () => {\n    const startIndex = currentPage * dataLimit - dataLimit\n    const endIndex = startIndex + dataLimit\n    return arr?.slice(startIndex, endIndex)\n}\n\n{getPaginatedData() && (\n    <Grid col={4} gap="s">\n        {getPaginatedData().map(text => (\n            <Card border={{ width: 1 }} key={uuid()}>\n                <Text>{text}</Text>\n            </Card>\n        ))}\n    </Grid>\n)}\n\n{totalPages > 1 && (\n    <Paginator\n        totalPages={totalPages}\n        page={currentPage}\n        handlePrevPage={handlePrev}\n        handleNextPage={handleNext}\n        handleInput={handlePage}\n    />\n)}',
        },
    ]

    const requiredCustom = (
        <>
            This is required for the <code>custom Paginator</code> component.
        </>
    )

    const propsItems: PropsItemType[] = [
        {
            subtitle: "totalPages",
            type: "number",
            required: true,
        },
        {
            subtitle: "page",
            intro: requiredCustom,
            type: "number",
        },
        {
            subtitle: "handlePrevPage",
            intro: requiredCustom,
            type: "function",
        },
        {
            subtitle: "handleNextPage",
            intro: requiredCustom,
            type: "function",
        },
        {
            subtitle: "handleInput",
            intro: requiredCustom,
            type: "function",
        },
        {
            subtitle: "queries",
            intro: (
                <>
                    This is possible only when not using the{" "}
                    <code>custom Paginator</code> component.
                </>
            ),
            type: "array of array of strings",
        },
        {
            subtitle: "isInputEditable",
            type: "boolean",
        },
        "separator",
        {
            subtitle: "texts",
            type: "object",
        },
        {
            title: (
                <>
                    <code>texts</code> object
                </>
            ),
            subtitle: "page",
            type: "string",
            default: "Page",
        },
        {
            subtitle: "of",
            type: "string",
            default: "of",
        },
        "separator",
        {
            subtitle: "icons",
            type: "object",
        },
        {
            title: (
                <>
                    <code>icons</code> object
                </>
            ),
            subtitle: "prev",
            intro: commonProps.addIconCapital,
            type: "string",
            default: iconBoxicon("chevron-left"),
        },
        {
            subtitle: "next",
            type: "string",
            default: iconBoxicon("chevron-right"),
        },
        {
            subtitle: "size",
            type: "number",
            default: 24,
        },
        "separator",
        {
            subtitle: "buttons",
            type: "object",
        },
        {
            title: (
                <>
                    <code>buttons</code> object
                </>
            ),
            subtitle: "labelPrev",
            type: "string",
            default: "Previous page",
        },
        {
            subtitle: "labelNext",
            type: "string",
            default: "Next page",
        },
        {
            subtitle: "showLabel",
            type: "boolean",
        },
    ]

    return <ComponentsLayout items={items} propsItems={propsItems} />
}

export default PaginatorPage
